import React from 'react';

const KEY = 'disclaimerAccepted';

export const alreadyAccepted = () => {
  try {
    return sessionStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Bottom sheet shown once per browser session. There is deliberately no way to
 * dismiss it except Accept: declining means not using the service. The
 * parent owns `accepted` so nothing (including /ask?q= links) is sent before it.
 */
export const DisclaimerSheet: React.FC<{
  accepted: boolean;
  onAccept: () => void;
}> = ({ accepted, onAccept }) => {
  const button = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (!accepted) button.current?.focus();
  }, [accepted]);

  if (accepted) return null;

  const accept = () => {
    try {
      sessionStorage.setItem(KEY, '1');
    } catch {
      // storage blocked: the sheet just comes back next visit
    }
    onAccept();
  };

  // One focusable element, so keep focus on it and ignore Escape.
  const keepFocus = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      button.current?.focus();
    }
  };

  return (
    // The scrim lets the chat show through but blocks clicks behind the sheet.
    <div
      className="fixed inset-0 z-50 bg-ground/40 backdrop-blur-sm"
      onKeyDown={keepFocus}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="disclaimer-title"
        aria-describedby="disclaimer-body"
        className="sheet-up absolute inset-x-0 bottom-0 flex min-h-[34svh] flex-col justify-end rounded-t-container border-t border-line bg-ground/70 backdrop-blur-2xl"
      >
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 pb-8 pt-8 text-left">
          <div className="flex flex-col gap-3">
            <h2 id="disclaimer-title" className="font-display text-heading">
              This is AI, and it can be wrong
            </h2>
            <p id="disclaimer-body" className="max-w-xl text-body text-muted">
              By using Marja AI you understand that its answers are AI-generated
              and may be inaccurate. Use them as guidance to research the topic
              further, and check the sources or ask a scholar.
            </p>
          </div>
          <button
            ref={button}
            type="button"
            onClick={accept}
            className="self-start rounded-full bg-peach px-9 py-4 text-button text-navy transition-colors hover:bg-peach-soft"
          >
            Accept and continue
          </button>
        </div>
      </div>
    </div>
  );
};
